/**
 * Mobile floating add-to-cart button.
 * Shows a fixed bottom bar when the in-page buy button scrolls out of view,
 * and hides when the footer enters the viewport.
 */
(function () {
  const MOBILE_MEDIA_QUERY = window.matchMedia("(max-width: 1023px)");

  function getProductInfoEl(sectionId) {
    return document.getElementById(`ProductInfo-${sectionId}`);
  }

  function getSizeFieldset(container) {
    if (!container) return null;

    return Array.from(container.querySelectorAll("variant-selects fieldset")).find((fieldset) => {
      const legendText = fieldset.querySelector("legend")?.textContent?.toLowerCase() || "";
      return legendText.includes("size");
    });
  }

  function getSizeSwatchContainer(container) {
    if (!container) return null;

    const optionGroups = container.querySelectorAll(".swatch-option, .swatch-drop-down, [class*='swatch']");
    for (const group of optionGroups) {
      const labelText =
        group.querySelector(".swatch-option-name, .swatch-label, .swatch-label-button")?.textContent?.toLowerCase() ||
        "";
      if (labelText.includes("size")) {
        return group;
      }
    }

    const swatchViews = container.querySelectorAll(".swatch-view:has(.star-set-btn)");
    return swatchViews.length ? swatchViews[swatchViews.length - 1] : null;
  }

  function hasSwatchPicker(productInfo) {
    return Boolean(
      productInfo.querySelector(".swatch-view .star-set-btn, .swatch-drop-down, .swatch-option .star-set-btn")
    );
  }

  function requiresSizeSelection(sectionId) {
    const floatingEl = document.getElementById(`ProductFormButtonsFloating-${sectionId}`);
    return Boolean(floatingEl?.querySelector("[data-requires-size-selection]"));
  }

  function isSizeSelected(sectionId) {
    const productInfo = getProductInfoEl(sectionId);
    const needsSize = requiresSizeSelection(sectionId);

    if (!productInfo) return !needsSize;

    const sizeSwatchContainer = getSizeSwatchContainer(productInfo);
    if (sizeSwatchContainer) {
      return Boolean(sizeSwatchContainer.querySelector(".star-set-btn.swatch-selected"));
    }

    // Product requires size — wait for an explicit swatch selection, not native defaults
    if (needsSize || hasSwatchPicker(productInfo)) {
      return false;
    }

    const sizeFieldset = getSizeFieldset(productInfo);
    if (sizeFieldset) {
      return Boolean(sizeFieldset.querySelector('input[type="radio"]:checked'));
    }

    return true;
  }

  function getSizeScrollTarget(container) {
    return getSizeSwatchContainer(container) || getSizeFieldset(container);
  }

  function scrollToSizeSelector(sectionId) {
    const productInfo = getProductInfoEl(sectionId);
    if (!productInfo) return;

    const target = getSizeScrollTarget(productInfo);
    if (target) {
      target.scrollIntoView({ block: "center", behavior: "smooth" });
      return;
    }

    productInfo.scrollIntoView({ block: "start", behavior: "smooth" });
  }

  function setupScrollLink(instance) {
    const scrollLink = instance.floatingEl.querySelector("[data-floating-scroll-link]");
    if (!scrollLink || scrollLink.dataset.scrollBound) return;

    scrollLink.dataset.scrollBound = "true";
    scrollLink.addEventListener("click", (event) => {
      event.preventDefault();
      scrollToSizeSelector(instance.sectionId);
    });
  }

  function updateFloatingBuyState(instance) {
    const scrollLink = instance.floatingEl.querySelector("[data-floating-scroll-link]");
    const buyButton = instance.floatingEl.querySelector("[data-floating-buy-button]");
    if (!scrollLink || !buyButton) return;

    const showScrollLink = !isSizeSelected(instance.sectionId);
    scrollLink.classList.toggle("hidden", !showScrollLink);
    buyButton.classList.toggle("hidden", showScrollLink);
  }

  class FixedAtcButton {
    constructor() {
      this.instances = [];
      this.footer = null;
      this.footerInView = false;
      this.footerObserver = null;

      this.onMediaChange = this.onMediaChange.bind(this);

      MOBILE_MEDIA_QUERY.addEventListener("change", this.onMediaChange);

      this.init();
    }

    init() {
      this.footer = document.querySelector("footer.footer") || document.querySelector("footer");
      this.instances = [];

      document.querySelectorAll('[id^="ProductFormButtonsFloating-"]').forEach((floatingEl) => {
        const sectionId = floatingEl.id.replace("ProductFormButtonsFloating-", "");
        const fixedEl = document.getElementById(`ProductFormButtons-${sectionId}`);
        if (!fixedEl) return;

        const instance = {
          sectionId,
          floatingEl,
          fixedEl,
          wantsFloating: false,
          observer: null,
          syncObserver: null,
        };

        this.instances.push(instance);
        this.setupButtonObserver(instance);
        this.setupSizeListeners(instance);
        setupScrollLink(instance);
        updateFloatingBuyState(instance);
      });

      this.setupFooterObserver();
      this.resetDesktopState();
    }

    setupSizeListeners(instance) {
      const productInfo = getProductInfoEl(instance.sectionId);
      const update = () => updateFloatingBuyState(instance);

      window.subscribe?.(window.PUB_SUB_EVENTS?.variantChange, () => {
        queueMicrotask(update);
      });

      window.subscribe?.(window.PUB_SUB_EVENTS?.optionValueSelectionChange, () => {
        queueMicrotask(update);
      });

      instance.syncObserver?.disconnect();
      if (!productInfo) return;

      instance.syncObserver = new MutationObserver(update);
      instance.syncObserver.observe(productInfo, {
        attributes: true,
        subtree: true,
        attributeFilter: ["class", "aria-checked"],
      });
    }

    setupButtonObserver(instance) {
      const { fixedEl } = instance;

      instance.observer?.disconnect();
      instance.observer = new IntersectionObserver(
        (entries) => {
          if (!MOBILE_MEDIA_QUERY.matches) return;

          entries.forEach((entry) => {
            instance.wantsFloating = !entry.isIntersecting;
            this.syncInstanceVisibility(instance);
          });
        },
        { threshold: 0, rootMargin: "0px" }
      );

      instance.observer.observe(fixedEl);
    }

    setupFooterObserver() {
      this.footerObserver?.disconnect();
      this.footerInView = false;

      if (!this.footer) return;

      this.footerObserver = new IntersectionObserver(
        (entries) => {
          if (!MOBILE_MEDIA_QUERY.matches) return;

          entries.forEach((entry) => {
            this.footerInView = entry.isIntersecting;
            this.instances.forEach((instance) => this.syncInstanceVisibility(instance));
          });
        },
        { threshold: 0, rootMargin: "0px" }
      );

      this.footerObserver.observe(this.footer);
    }

    syncInstanceVisibility(instance) {
      const { floatingEl, fixedEl } = instance;
      const showFloating = instance.wantsFloating && !this.footerInView;

      floatingEl.classList.toggle("atc-floating-button-hidden", !showFloating);
      floatingEl.classList.toggle("atc-floating-button-visible", showFloating);
      fixedEl.classList.toggle("fixed-atc-button-hidden", showFloating);
      fixedEl.classList.toggle("fixed-atc-button-visible", !showFloating);

      floatingEl.style.bottom = "";
    }

    resetDesktopState() {
      if (MOBILE_MEDIA_QUERY.matches) return;

      this.instances.forEach((instance) => {
        const { floatingEl, fixedEl } = instance;
        floatingEl.classList.add("atc-floating-button-hidden");
        floatingEl.classList.remove("atc-floating-button-visible");
        floatingEl.style.bottom = "";
        fixedEl.classList.remove("fixed-atc-button-hidden");
        fixedEl.classList.add("fixed-atc-button-visible");
      });
    }

    onMediaChange() {
      if (MOBILE_MEDIA_QUERY.matches) {
        this.instances.forEach((instance) => {
          this.setupButtonObserver(instance);
          updateFloatingBuyState(instance);
        });
        this.setupFooterObserver();
      } else {
        this.resetDesktopState();
      }
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => new FixedAtcButton());
  } else {
    new FixedAtcButton();
  }
})();
